import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Search } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TraineesPage() {
  const navigate = useNavigate();
  const [trainees, setTrainees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data: agents } = await axios.get(`${API}/users?role=agent`);
        const withProgress = await Promise.all(
          agents.map(async (a) => {
            try {
              const { data: prog } = await axios.get(`${API}/journey/progress/${a.id}`);
              const completed = prog.filter(p => p.status === 'completed' || p.status === 'approved').length;
              const inProgress = prog.filter(p => p.status === 'in_progress').length;
              return { ...a, completed_days: completed, in_progress_days: inProgress };
            } catch {
              return { ...a, completed_days: 0, in_progress_days: 0 };
            }
          })
        );
        setTrainees(withProgress);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = trainees.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.username.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0047FF] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div data-testid="trainees-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] tracking-tight">Trainees</h1>
        <p className="font-body text-sm text-[#4B5563] mt-1">{trainees.length} trainees total</p>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
        <Input
          data-testid="trainees-search"
          placeholder="Search trainees..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 rounded-none border-[#E5E7EB] font-body"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(t => {
          const pct = Math.round((t.completed_days / 16) * 100);
          const isGraduated = t.completed_days >= 16;
          return (
            <div
              key={t.id}
              data-testid={`trainee-card-${t.id}`}
              onClick={() => navigate(`/trainees/${t.id}`)}
              className="border border-[#E5E7EB] bg-white p-5 cursor-pointer hover:border-[#0047FF] transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading text-base font-semibold text-[#0A0A0A]">{t.name}</h3>
                  <p className="font-body text-sm text-[#4B5563]">@{t.username}</p>
                </div>
                {isGraduated && (
                  <Badge className="rounded-none bg-[#16A34A] text-white font-body text-xs">Graduated</Badge>
                )}
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-body text-xs text-[#4B5563]">Progress</span>
                  <span className="font-body text-xs font-semibold text-[#0A0A0A]">{t.completed_days}/16</span>
                </div>
                <Progress value={pct} className="h-2" />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#E5E7EB]">
                <span className="font-body text-xs text-[#4B5563]">
                  Trainer: {t.assigned_trainer_name || 'Unassigned'}
                </span>
                {t.in_progress_days > 0 && (
                  <Badge variant="outline" className="rounded-none font-body text-xs text-[#0047FF] border-[#0047FF]">Active</Badge>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="font-body text-sm text-[#4B5563] col-span-full text-center py-8">No trainees found</p>
        )}
      </div>
    </div>
  );
}
